import type { Accept } from "react-dropzone";
import FileUploader from "./FileUploader";

type InputType =
  | "email"
  | "text"
  | "number"
  | "password"
  | "search"
  | "url"
  | "date"
  | "time"
  | "datetime-local"
  | "month";

const inputClassName = `
    block w-full outline-none
    border-b
    border-b-grey-border
    text-sm
    font-Metropolis
    dark:bg-bg-dark
    dark:text-grey-light
    dark:border-b-grey-darker
    px-[.5rem] py-[.625rem] mt-0.5
  `;

export const Label: React.FC<
  {
    label: string;
    classNames?: string;
    children?: any;
  } & React.LabelHTMLAttributes<HTMLLabelElement>
> = ({ label, classNames = "", children, ...props }) => (
  <label
    className={`text-sm font-bold text-grey-darker uppercase ${classNames}`}
    {...props}
  >
    {label}
    {children}
  </label>
);

export const Input: React.FC<
  {
    className?: string;
    type?: InputType;
    name: string;
  } & React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >
> = ({ className = "", type = "text", name, autoComplete, ...props }) => {
  const inputClasses = `${inputClassName} ${className}`;
  return (
    <input
      type={type}
      name={name}
      className={inputClasses}
      autoComplete={autoComplete}
      {...props}
    />
  );
};

export const Select: React.FC<
  {
    className?: string;
    name: string;
    options: { name: string; value: string }[];
    selected?: string[];
  } & React.DetailedHTMLProps<
    React.SelectHTMLAttributes<HTMLSelectElement>,
    HTMLSelectElement
  >
> = ({ className, name, autoComplete, options, selected, ...props }) => {
  const inputClasses = `${inputClassName} ${className}`;

  return (
    <select
      name={name}
      className={inputClasses}
      autoComplete={autoComplete}
      {...props}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value} selected={selected && selected.includes(opt.value)}>
          {opt.name}
        </option>
      ))}
    </select>
  );
};

export const SearchableSelect: React.FC<
  {
    className?: string;
    name: string;
    options: { name: string; value: string }[];
    selected?: string[];
  } & React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >
> = ({ className, name, autoComplete, options, selected, ...props }) => {
  const inputClasses = `${inputClassName} ${className}`;

  return (
    <>
      <input
        list={`${name}-id`}
        id={`${name}-input`}
        name={name}
        className={inputClasses}
        {...props}
      />
      <datalist id={`${name}-id`}>
        {options.map((opt) => (
          <option
            key={opt.value}
            value={opt.value}
            selected={selected && selected.includes(opt.value)}
          >
            {opt.name}
          </option>
        ))}
      </datalist>
    </>
  );
};

export const Textarea: React.FC<
  {
    className?: string;
    name: string;
  } & React.DetailedHTMLProps<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    HTMLTextAreaElement
  >
> = ({ className, name, autoComplete, ...props }) => {
  const inputClasses = `${inputClassName} ${className}`;

  return (
    <textarea
      name={name}
      className={inputClasses}
      autoComplete={autoComplete}
      {...props}
    />
  );
};

export const FileUpload: React.FC<{
  handleUpload: Function;
  fileTypes?: Accept;
  name: string;
  maxFiles?: number;
  maxSize?: number;
  uploadName: string;
}> = ({
  handleUpload,
  fileTypes = "image/*,application/pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.wordprocessingml.template",
  uploadName,
  maxFiles = 1,
  maxSize,
  name,
}) => {
  return (
    <FileUploader
      handleUpload={handleUpload}
      // @ts-ignore
      fileTypes={fileTypes}
      uploadName={uploadName}
      maxFiles={maxFiles}
      maxSize={maxSize}
      name={name}
    />
  );
};

export const InputField: React.FC<
  {
    label: string;
    name: string;
    type: InputType;
    className?: string;
  } & React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >
> = ({ label, type, name, className, ...props }) => {
  return (
    <p className="justify-start items-start my-[1rem]">
      <Label label={label} htmlFor={name} />
      <Input type={type} name={name} className={className} {...props} />
    </p>
  );
};

export const TextareaField: React.FC<
  {
    label: string;
    name: string;
    type: InputType;
    className?: string;
  } & React.DetailedHTMLProps<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    HTMLTextAreaElement
  >
> = ({ label, type, name, className, ...props }) => {
  return (
    <p className="justify-start items-start my-[1rem]">
      <Label label={label} htmlFor={name} />
      <Textarea name={name} className={className} {...props} />
    </p>
  );
};

export const SelectField: React.FC<
  {
    label: string;
    name: string;
    className?: string;
    options: { name: string; value: string }[];
    selected?: string[]
  } & React.DetailedHTMLProps<
    React.SelectHTMLAttributes<HTMLSelectElement>,
    HTMLSelectElement
  >
> = ({ label, name, className, options, ...props }) => {
  return (
    <p className="justify-start items-start my-[1rem]">
      <Label label={label} htmlFor={name} />
      <Select name={name} className={className} options={options} {...props} />
    </p>
  );
};

export const SearchableSelectField: React.FC<
  {
    label: string;
    name: string;
    className?: string;
    parentClassName?: string;
    options: { name: string; value: string }[];
    selected?: string[];
  } & React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >
> = ({ label, name, className, parentClassName, options, ...props }) => {
  return (
    <p className={`justify-start items-start my-[1rem] ${parentClassName}`}>
      <Label label={label} htmlFor={name} />
      <SearchableSelect
        name={name}
        className={className}
        options={options}
        {...props}
      />
    </p>
  );
};
